/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import bgQuestionSingleChoice from 'assets/images/backgrounds/question-type/bg-single-choice.jpg';
import CloseIcon from '@mui/icons-material/Close';
import BrandLogo from 'common/components/brand-logo';
import BaseTextField from 'common/components/base-text-field';
import FormProvider from 'common/components/form-provider';
import { QuestionType } from 'pages/admin/campaign-overview/constants';
import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { createStyleSheet, handleError } from 'utils/helpers';
import * as yup from 'yup';
import QuestionCheckbox from '../question/question-checkbox';
import QuestionDate from '../question/question-date';
import QuestionDropdown from '../question/question-dropdown';
import QuestionEmoji from '../question/question-emoji';
import QuestionImage from '../question/question-image';
import QuestionImageWithTitle from '../question/question-image-with-title';
import QuestionMatrixCheckbox from '../question/question-matrix-checkbox';
import QuestionMatrixRadio from '../question/question-matrix-radio';
import QuestionMatrixTextbox from '../question/question-matrix-textbox';
import QuestionNumber from '../question/question-number';
import QuestionRank from '../question/question-rank';
import QuestionScore from '../question/question-score';
import QuestionScoreStar from '../question/question-score-star';
import QuestionSingleShoice from '../question/question-single-choice';
import QuestionSliderScale from '../question/question-slider-scale';
import AnswerQuestionTextbox from '../question/question-textbox';
import QuestionTextboxRecommend from '../question/question-textbox-recommend';
import QuestionTextboxRecommendMultiChoice from '../question/question-textbox-recommend-multi-choice';
import { colors } from 'common/constants';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hook';
import {
    selectSurveyImage,
    selectWelcomeTextColor,
} from 'common/theme-selectors';
import { captureTimeStamp as captureTimeStampAction } from 'slices/previewLogicPageSurveySlice';
import { createAnswers } from 'services/previewCampaign.service';
import { setIsLoading } from 'slices/loadingSlice';
import {
    IPreviewSurveyState,
    IQuestionPreview,
    IQuestionScreening,
    IQuestionBranch,
} from 'types/previewCampaign';
import { getQuestionBranchLogicOptions } from 'services/campaign.service';
import { previewQuestionFormatter } from 'utils/preview_helpers';
import LinkIcon from '@mui/icons-material/Link';

const fileMaxSize = 20;
const fileMaxSizeInByte = fileMaxSize * 1024 * 1024;
const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];

const textRequire = 'Vui lòng trả lời câu hỏi này';

const CHOOSE = 1;
const NOT_CHOOSE = 0;

// Validation functions
const imageYupPreparation = (question: IQuestionPreview, required: boolean) => {
    let baseAnswerYup = yup
        .mixed()
        .test(
            'fileType',
            'Ảnh không đúng định dạng, vui lòng tải ảnh có định dạng sau: .png, .jpg, .jpeg ',
            (value) => {
                if (!value) return true;
                const isValidType =
                    value instanceof File &&
                    SUPPORTED_FORMATS.includes(value.type);
                return isValidType;
            }
        )
        .test(
            'fileSize',
            `File không được vượt quá ${fileMaxSize}MB`,
            (value) => {
                if (!value) return true;
                return value instanceof File && value.size <= fileMaxSizeInByte;
            }
        );
    if (required) {
        baseAnswerYup = baseAnswerYup.test(
            'is-required',
            textRequire,
            (value) => {
                return value instanceof File;
            }
        );
    }
    return yup.object({
        answers: baseAnswerYup,
        questionId: yup.number(),
    });
};

const imageWithTitleYupPreparation = (
    question: IQuestionPreview,
    required: boolean
) => {
    const baseAnswersYup = yup
        .array()
        .of(
            yup.object({
                image: yup
                    .mixed()
                    .nullable()
                    .test(
                        'fileType',
                        'Ảnh không đúng định dạng, vui lòng tải ảnh có định dạng sau: .png, .jpg, .jpeg ',
                        (file) => {
                            if (!file) return true;
                            return (
                                file instanceof File &&
                                SUPPORTED_FORMATS.includes(file.type)
                            );
                        }
                    )
                    .test(
                        'fileSize',
                        `File không được vượt quá ${fileMaxSize}MB`,
                        (file) => {
                            if (!(file instanceof File)) return true;
                            return file.size <= fileMaxSizeInByte;
                        }
                    ),
            })
        )
        .test('has-image-if-required', textRequire, (value) => {
            if (!required) return true;
            return (
                Array.isArray(value) &&
                value.some((item) => item?.image instanceof File)
            );
        });

    return yup.object({
        answers: baseAnswersYup,
        questionId: yup.number(),
    });
};

const matrixCheckBoxYupPreparation = (
    question: IQuestionPreview,
    required: boolean
) => {
    let baseAnswersYup = yup.array().of(yup.array().of(yup.boolean()));
    if (required) {
        baseAnswersYup = baseAnswersYup.test(
            'is-required',
            textRequire,
            (value) => {
                return (
                    Array.isArray(value) &&
                    value?.every((item) =>
                        item?.some((subItem) => subItem === true)
                    )
                );
            }
        );
    }
    return yup.object({
        answers: baseAnswersYup,
        questionId: yup.number(),
    });
};

const checkBoxQuestionYupPreparation = (
    question: IQuestionPreview,
    required: boolean
) => {
    const { minChoice, maxChoice } = question.logics || {};
    let baseAnswersYup = yup
        .array()
        .of(yup.number())
        .test(
            'noAnswerOrOtherAnswers',
            'Không được chọn cùng lúc "Không có đáp án nào" và các đáp án trên đó',
            (value) => {
                const listOptionTypeInValue: number[] = [];
                if (!value) return true;
                if (value.length === 0) return true;
                value.forEach((item) => {
                    const option = question.answers.find(
                        (answer: any) => answer.id === item // eslint-disable-line @typescript-eslint/no-explicit-any
                    );
                    if (option) {
                        listOptionTypeInValue.push(option.type);
                    } else {
                        listOptionTypeInValue.push(0);
                    }
                });
                // if listOptionTypeInValue include 2 then it can not include 1 or 0
                if (listOptionTypeInValue.includes(2)) {
                    if (
                        listOptionTypeInValue.includes(1) ||
                        listOptionTypeInValue.includes(0)
                    ) {
                        return false;
                    }
                }
                return true;
            }
        );
    if (required) {
        baseAnswersYup = baseAnswersYup.test(
            'is-required',
            textRequire,
            (value) => {
                return value && value?.length > 0;
            }
        );
    }
    if (minChoice) {
        baseAnswersYup = baseAnswersYup.test(
            'min-choice',
            `Chọn tối thiểu ${minChoice} đáp án`,
            (value) => {
                if (!value) return true;
                if (value.length === 0) return true;

                return value && value?.length >= (minChoice || 0);
            }
        );
    }
    if (maxChoice) {
        baseAnswersYup = baseAnswersYup.test(
            'max-choice',
            `Chọn tối đa ${maxChoice} đáp án`,
            (value) => {
                if (!value) return true;
                if (value.length === 0) return true;

                return value && value?.length <= (maxChoice || 1);
            }
        );
    }
    return yup.object({
        answers: baseAnswersYup,
        questionId: yup.number(),
        answersOtherText: yup.string().when('answers', {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            is: (val: any) => {
                const otherOption = question.answers.find(
                    (item: any) => item.type === 1 // eslint-disable-line @typescript-eslint/no-explicit-any
                );
                if (!otherOption) return false;
                return (
                    val && Array.isArray(val) && val.includes(otherOption.id)
                );
            },
            then: (schema) => schema.required('Vui lòng nhập'),
        }),
    });
};

interface IAnswerTheSurveyProps {
    setSurveyStep?: (step: number) => void;
}

interface IPageData {
    id: number;
    page_number?: number;
    title?: string;
    questions: IQuestionPreview[];
}

const AnswerTheSurvey: React.FC<IAnswerTheSurveyProps> = ({
    setSurveyStep,
}) => {
    const dispatch = useAppDispatch();
    const isSubmittingRef = useRef(false);
    const campaignId = useParams<{ id: string }>().id;
    const query = new URLSearchParams(useLocation().search);
    const token = query.get('token');
    const me = useAppSelector((state) => state.previewLogicPageSurvey.me);
    const geoCoords = useAppSelector((state) => state.geoLocation.coords);
    const surveyImage = useAppSelector(selectSurveyImage);
    const welcomeTextColor = useAppSelector(selectWelcomeTextColor);

    const pagesData = useAppSelector(
        (state) => state.previewLogicPageSurvey.question_pages
    ) as IPageData[];

    const captureTimeStamp = useAppSelector(
        (state) => state.previewLogicPageSurvey.capturePerformanceTimeByQuestion
    );
    const [internalClockTimeStamp, setInternalClockTimeStamp] =
        React.useState(0);
    const surveyState = useAppSelector(
        (state) => state.previewLogicPageSurvey.surveyState
    );

    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    const [pageHistory, setPageHistory] = useState<number[]>([]);
    const [branchingQueue, setBranchingQueue] = useState<number[]>([]);
    const [savedAnswersData, setSavedAnswersData] = useState<
        Record<string, any>
    >({});

    // State để lưu branch questions: key là questionId của parent, value là array các branch questions
    const [branchQuestions, setBranchQuestions] = useState<
        Record<number, IQuestionPreview[]>
    >({});

    const currentPage: IPageData = pagesData?.[currentPageIndex] || {
        id: 0,
        questions: [],
    };
    const currentPageQuestions = currentPage.questions || [];

    const allQuestions = useMemo(() => {
        const baseQuestions = (pagesData || []).flatMap(
            (p) => p.questions || []
        );

        // Thêm branch questions vào allQuestions
        const branchQuestionsArray = Object.values(branchQuestions).flat();

        setInternalClockTimeStamp(performance.now());

        return [...baseQuestions, ...branchQuestionsArray];
    }, [pagesData, branchQuestions]);

    // Tạo danh sách questions hiển thị bao gồm cả branch questions
    // Hỗ trợ recursive branching (branch của branch)
    const displayedQuestions = useMemo(() => {
        const result: IQuestionPreview[] = [];
        const addedIds = new Set<number>();

        // Function đệ quy để thêm question và branches của nó
        const addQuestionWithBranches = (question: IQuestionPreview) => {
            // Tránh duplicate
            if (addedIds.has(question.id)) return;

            // Thêm question hiện tại
            result.push(question);
            addedIds.add(question.id);

            // Thêm các branch questions của nó (đệ quy)
            const branches = branchQuestions[question.id];
            if (branches && branches.length > 0) {
                branches.forEach((branchQ) => {
                    addQuestionWithBranches(branchQ); // ← Đệ quy
                });
            }
        };

        // Bắt đầu từ parent questions
        currentPageQuestions.forEach((question) => {
            addQuestionWithBranches(question);
        });

        return result;
    }, [currentPageQuestions, branchQuestions]);

    const createValidationSchemaAndDefaults = (
        questions: IQuestionPreview[]
    ) => {
        const shape: Record<string, yup.Schema<any>> = {};
        const defaultValues: Record<string, any> = {};

        questions.forEach((question: IQuestionPreview) => {
            const questionKey = `question_${question.id}`;
            switch (question.type) {
                case QuestionType.Textbox:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .string()
                            .required(textRequire)
                            .test('is-empty-string', textRequire, (value) => {
                                return value?.trim() !== '';
                            }),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: '',
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Checkbox:
                    shape[questionKey] = checkBoxQuestionYupPreparation(
                        question,
                        true
                    );
                    defaultValues[questionKey] = {
                        answers: [],
                        questionId: question?.id,
                        answersOtherText: '',
                    };
                    break;
                case QuestionType.SingleChoice:
                    shape[questionKey] = yup.object({
                        answers: yup.number().required(textRequire),
                        questionId: yup.number(),
                        answersOtherText: yup.string().when('answers', {
                            is: (val: any) => {
                                const otherOption = question.answers.find(
                                    (item: any) => item.type === 1
                                );
                                if (!otherOption) return false;
                                return Number(val) === otherOption.id;
                            },
                            then: (schema) => schema.required('Vui lòng nhập'),
                        }),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                        answersOtherText: '',
                    };
                    break;
                case QuestionType.Score:
                    shape[questionKey] = yup.object({
                        answers: yup.number().required(textRequire),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.ScoreStarHalf:
                case QuestionType.ScoreStarFull:
                    shape[questionKey] = yup.object({
                        answers: yup.array().of(
                            yup.object({
                                star: yup.number().required(textRequire),
                                id: yup.number(),
                            })
                        ),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: question.answers.map(
                            (answer: { id: number; label: string }) => ({
                                star: null,
                                id: answer.id,
                            })
                        ),
                        questionId: question.id,
                    };
                    break;
                case QuestionType.SliderScale:
                    shape[questionKey] = yup.object({
                        answers: yup.array().of(
                            yup.object({
                                score: yup.number(),
                                id: yup.number(),
                            })
                        ),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: question.answers.map(
                            (answer: { id: number; label: string }) => ({
                                score: 0,
                                id: answer.id,
                            })
                        ),
                        questionId: question.id,
                    };
                    break;
                case QuestionType.Rank:
                    shape[questionKey] = yup.object({
                        answers: yup.array().of(
                            yup.object({
                                id: yup.number(),
                                label: yup.string(),
                                position: yup.number().required(textRequire),
                            })
                        ),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [...question.answers],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.MatrixCheckbox:
                    shape[questionKey] = matrixCheckBoxYupPreparation(
                        question,
                        true
                    );
                    defaultValues[questionKey] = {
                        answers: [...question.answers.matrix],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.MatrixRadio:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .array()
                            .of(yup.string())
                            .test('is-required', textRequire, (value) => {
                                return (
                                    Array.isArray(value) &&
                                    value?.every(
                                        (item) =>
                                            typeof item === 'string' &&
                                            (item as string).length > 0
                                    )
                                );
                            }),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [...question.answers.matrix],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.MatrixText:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .array()
                            .of(yup.array().of(yup.string()))
                            .test('is-required', textRequire, (value) => {
                                return (
                                    Array.isArray(value) &&
                                    value?.every((item) =>
                                        item?.every(
                                            (subItem) =>
                                                typeof subItem === 'string' &&
                                                subItem.length > 0
                                        )
                                    )
                                );
                            }),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [...question.answers.matrix],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Dropdown:
                    shape[questionKey] = yup.object({
                        answers: yup.object().required(textRequire),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Number:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .number()
                            .typeError('Vui lòng nhập số')
                            .required(textRequire),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: null,
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.TextboxRecommend:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .object({
                                label: yup.string(),
                                value: yup
                                    .number()
                                    .typeError(textRequire)
                                    .required(textRequire),
                            })
                            .required(textRequire),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: { label: '', value: null },
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.TextboxRecommendMultiChoice:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .array()
                            .test('is-required', textRequire, (value) => {
                                return Array.isArray(value) && value.length > 0;
                            }),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: [],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Date:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .object({
                                day: yup.string().nullable(),
                                month: yup.string().nullable(),
                                year: yup.string().nullable(),
                            })
                            .test('is-required', textRequire, (value) => {
                                return !!(
                                    value &&
                                    value.day &&
                                    value.month &&
                                    value.year
                                );
                            }),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: {
                            day: '',
                            month: '',
                            year: '',
                        },
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Image:
                    shape[questionKey] = imageYupPreparation(question, true);
                    defaultValues[questionKey] = {
                        answers: '',
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.ImageWithTitle:
                    shape[questionKey] = imageWithTitleYupPreparation(
                        question,
                        true
                    );
                    defaultValues[questionKey] = {
                        answers: [],
                        questionId: question?.id,
                    };
                    break;
                case QuestionType.Emoji:
                    shape[questionKey] = yup.object({
                        answers: yup
                            .array()
                            .of(
                                yup.object({
                                    emoji: yup
                                        .string()
                                        .required(textRequire)
                                        .nullable(),
                                    id: yup.number(),
                                })
                            )
                            .test('is-required', textRequire, (value) => {
                                return (
                                    Array.isArray(value) &&
                                    value.every((item) => {
                                        return item.emoji !== '';
                                    })
                                );
                            }),
                        questionId: yup.number(),
                    });
                    defaultValues[questionKey] = {
                        answers: question.answers.map(
                            (answer: { id: number; label: string }) => ({
                                emoji: '',
                                id: answer.id,
                            })
                        ),
                        questionId: question.id,
                    };
                    break;
                default:
                    break;
            }
        });
        const validationSchema = yup.object().shape(shape);
        return { validationSchema, defaultValues };
    };

    // Tạo validation schema cho currentPageQuestions ban đầu
    const { validationSchema: initialSchema, defaultValues } =
        React.useMemo(() => {
            return createValidationSchemaAndDefaults(
                currentPageQuestions || []
            );
        }, [currentPageQuestions]);

    const methods = useForm({
        resolver: yupResolver(initialSchema),
        defaultValues: defaultValues,
        mode: 'onChange',
    });
    // Watch form values để filter branch questions dựa trên condition
    const formValues = methods.watch();

    // Filter displayedQuestions dựa trên condition
    const filteredDisplayedQuestions = React.useMemo(() => {
        // Helper: Tìm parent question của một branch question
        const findParentQuestion = (
            branchQ: IQuestionPreview
        ): IQuestionPreview | null => {
            for (const question of displayedQuestions) {
                const branches = branchQuestions[question.id];
                if (branches && branches.some((b) => b.id === branchQ.id)) {
                    return question;
                }
            }
            return null;
        };

        // Helper: Check nếu branch question nên được hiện
        const shouldShowBranch = (
            parentQuestion: IQuestionPreview,
            branchQuestion: IQuestionPreview
        ): boolean => {
            if (
                !parentQuestion.branchLogic ||
                parentQuestion.branchLogic.length === 0
            ) {
                return true;
            }

            // Tìm logic liên quan đến branch question này
            const relevantLogics = parentQuestion.branchLogic.filter(
                (logic: IQuestionBranch) =>
                    logic.branch_question_id === branchQuestion.id
            );

            if (relevantLogics.length === 0) return true;

            // Lấy user's answer cho parent question
            const questionKey = `question_${parentQuestion.id}`;
            const userAnswer = formValues[questionKey]?.answers;

            // Check từng logic
            for (const logic of relevantLogics) {
                if (logic.condition === CHOOSE) {
                    // condition = 1: Hiện nếu user CHỌN answer này
                    if (
                        parentQuestion.type === QuestionType.SingleChoice ||
                        parentQuestion.type === QuestionType.Dropdown
                    ) {
                        const selectedValue = Number(
                            userAnswer || (userAnswer as any)?.value
                        );
                        if (selectedValue === Number(logic.answer_id)) {
                            return true;
                        }
                    } else if (parentQuestion.type === QuestionType.Checkbox) {
                        const selectedAnswers = Array.isArray(userAnswer)
                            ? userAnswer.map((id: any) => Number(id))
                            : [];
                        if (selectedAnswers.includes(Number(logic.answer_id))) {
                            return true;
                        }
                    }
                } else if (logic.condition === NOT_CHOOSE) {
                    // condition = 0: ẨN nếu user CHỌN answer này
                    if (
                        parentQuestion.type === QuestionType.SingleChoice ||
                        parentQuestion.type === QuestionType.Dropdown
                    ) {
                        const selectedValue = Number(
                            userAnswer || (userAnswer as any)?.value
                        );
                        if (selectedValue === Number(logic.answer_id)) {
                            return false; // ẨN đi
                        }
                    } else if (parentQuestion.type === QuestionType.Checkbox) {
                        const selectedAnswers = Array.isArray(userAnswer)
                            ? userAnswer.map((id: any) => Number(id))
                            : [];
                        if (selectedAnswers.includes(Number(logic.answer_id))) {
                            return false; // ẨN đi
                        }
                    }
                }
            }

            // Mặc định: HIỆN (cho NOT_CHOOSE khi chưa chọn gì)
            return true;
        };

        // Filter displayedQuestions
        return displayedQuestions.filter((question) => {
            const parent = findParentQuestion(question);
            if (!parent) {
                // Không phải branch question → luôn hiện
                return true;
            }
            // Là branch question → check condition
            return shouldShowBranch(parent, question);
        });
    }, [displayedQuestions, branchQuestions, formValues]);

    // Ref để track lần đầu tiên và tránh loop
    const initializedBranchQuestionsRef = React.useRef<Set<number>>(new Set());

    // Cache để tránh fetch duplicate
    const branchQuestionCacheRef = React.useRef<Map<number, IQuestionPreview>>(
        new Map()
    );

    // Ref để track đang fetch (tránh parallel duplicate calls)
    const fetchingBranchQuestionsRef = React.useRef<Set<number>>(new Set());

    // Register branch questions khi chúng xuất hiện (chỉ 1 lần cho mỗi question)
    React.useEffect(() => {
        Object.entries(branchQuestions).forEach(([, branches]) => {
            branches.forEach((branchQ) => {
                // Chỉ register nếu chưa được khởi tạo
                if (!initializedBranchQuestionsRef.current.has(branchQ.id)) {
                    const questionKey = `question_${branchQ.id}`;

                    // Kiểm tra xem đã có saved value chưa
                    const existingValue = methods.getValues()[questionKey];
                    const hasSavedValue =
                        savedAnswersDataRef.current?.[questionKey];

                    // CHỈ set default value nếu KHÔNG có saved value
                    if (!existingValue && !hasSavedValue) {
                        const { defaultValues: branchDefaults } =
                            createValidationSchemaAndDefaults([branchQ]);

                        const branchDefaultValue = branchDefaults[questionKey];
                        if (branchDefaultValue) {
                            methods.setValue(questionKey, branchDefaultValue, {
                                shouldValidate: false,
                                shouldDirty: false,
                            });
                        }
                    }

                    // Mark as initialized bất kể có set value hay không
                    initializedBranchQuestionsRef.current.add(branchQ.id);
                }
            });
        });
    }, [branchQuestions, methods]);

    // useEffect để xử lý logic uncheck khi chọn type = 2 (Không có đáp án nào)
    React.useEffect(() => {
        const subscription = methods.watch((value, { name: fieldName }) => {
            if (!fieldName || !fieldName.includes('.answers')) return;

            const questionKey = fieldName.split('.')[0];
            const questionIdStr = questionKey.split('_')[1];
            const questionId = Number(questionIdStr);

            const question = filteredDisplayedQuestions.find(
                (q) => q.id === questionId
            );

            if (question?.type !== QuestionType.Checkbox) return;

            const currentAnswers = value[questionKey]?.answers;
            if (!Array.isArray(currentAnswers)) return;

            const type2Option = question.answers.find(
                (answer: any) => answer.type === 2
            );

            if (!type2Option) return;

            const hasType2 = currentAnswers.includes(type2Option.id);
            const hasMultipleAnswers = currentAnswers.length > 1;

            if (hasType2 && hasMultipleAnswers) {
                methods.setValue(`${questionKey}.answers`, [type2Option.id], {
                    shouldValidate: true,
                });
            }
        });

        return () => subscription.unsubscribe();
    }, [filteredDisplayedQuestions, methods]);

    const {
        handleSubmit,
        formState: { isSubmitting, errors },
    } = methods;

    const formatDataBeforeSubmit = (data: any) => {
        const result: Record<string, any> = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                const element = data[key];
                const questionId = key.split('_')[1];
                if (questionId) {
                    const findQuestion = allQuestions.find(
                        (item) => item.id === Number(questionId)
                    );
                    if (findQuestion) {
                        const { type, answers: dataQuestion } = findQuestion;
                        if (type === QuestionType.MatrixCheckbox) {
                            const actualAnswer: string[] = [];
                            const displayRowsTitle =
                                dataQuestion.rowsTitle || [];
                            const displayColumnsTitle =
                                dataQuestion.columnsTitle || [];

                            if (
                                element.answers &&
                                displayRowsTitle &&
                                displayColumnsTitle
                            ) {
                                displayRowsTitle.forEach(
                                    (rowTitle: string, rowIndex: number) => {
                                        displayColumnsTitle.forEach(
                                            (
                                                columnTitle: string,
                                                colIndex: number
                                            ) => {
                                                if (
                                                    element.answers[rowIndex] &&
                                                    element.answers[rowIndex][
                                                        colIndex
                                                    ]
                                                ) {
                                                    actualAnswer.push(
                                                        `${rowTitle}&-&${columnTitle}`
                                                    );
                                                }
                                            }
                                        );
                                    }
                                );
                            }
                            element.answers = actualAnswer;
                        }
                        if (type === QuestionType.MatrixText) {
                            const { rowsTitle } = dataQuestion;
                            if (
                                element.answers &&
                                Array.isArray(element.answers) &&
                                rowsTitle
                            ) {
                                element.answers = element.answers.map(
                                    (row: string[], rowIndex: number) => {
                                        if (Array.isArray(row)) {
                                            row.unshift(rowsTitle[rowIndex]);
                                        }
                                        return row;
                                    }
                                );
                            }
                        }
                        if (type === QuestionType.Number) {
                            element.answers = (
                                element.answers || ''
                            ).toString();
                        }
                        if (type === QuestionType.ImageWithTitle) {
                            if (
                                findQuestion.answers &&
                                findQuestion.answers.images &&
                                element.answers &&
                                Array.isArray(element.answers)
                            ) {
                                const optionImages =
                                    findQuestion.answers.images;
                                element.answers = element.answers.map(
                                    (
                                        item: { image: File },
                                        itemIndex: number
                                    ) => {
                                        return {
                                            image: item.image,
                                            option_id:
                                                optionImages[itemIndex]?.value,
                                        };
                                    }
                                );
                            }
                        }
                        result[questionId] = element;
                    }
                }
            }
        }
        return result;
    };

    const submitAllAnswers = async (finalData: any) => {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        const timeStampPayload = { ...captureTimeStamp };
        const finalQuestionId = 'null';
        const finalTimeCapture = {
            questionId: finalQuestionId,
            time: performance.now() - internalClockTimeStamp,
        };
        if (timeStampPayload[finalQuestionId]) {
            timeStampPayload[finalQuestionId] += finalTimeCapture.time;
        } else {
            timeStampPayload[finalQuestionId] = finalTimeCapture.time;
        }
        try {
            dispatch(setIsLoading(true));
            const payload = formatDataBeforeSubmit(finalData);

            // Thu thập quota data từ tất cả các pages
            const quotas: any[] = [];
            pagesData.forEach((page) => {
                const pageQuestions = page.questions || [];
                const quotaAnswers: number[] = [];

                pageQuestions.forEach((q) => {
                    if (
                        q.type === QuestionType.Dropdown ||
                        q.type === QuestionType.SingleChoice
                    ) {
                        const questionKey = `question_${q.id}`;
                        const answer = finalData[questionKey]?.answers;

                        if (answer) {
                            const answerId =
                                q.type === QuestionType.Dropdown
                                    ? answer?.value
                                    : answer;

                            if (answerId) {
                                quotaAnswers.push(Number(answerId));
                            }
                        }
                    }
                });

                // Thêm quota object cho page này
                if (quotaAnswers.length > 0) {
                    quotas.push({
                        question_page_id: page.id,
                        answers: quotaAnswers,
                    });
                }
            });

            const res = await createAnswers(
                campaignId ?? '',
                payload,
                timeStampPayload,
                quotas.length > 0 ? quotas : undefined,
                geoCoords
            );

            if (res.success) {
                setSurveyStep && setSurveyStep(2);
                dispatch(setIsLoading(false));
                toast.success(res.message);
                isSubmittingRef.current = false;
            } else {
                dispatch(setIsLoading(false));
                isSubmittingRef.current = false;
                if (res.status_code === 3201) {
                    setSurveyStep && setSurveyStep(3);
                } else {
                    toast.error(res.message);
                }
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            isSubmittingRef.current = false;
            handleError(error);
        }
    };
    const onSubmit = handleSubmit(async (data) => {
        await submitAllAnswers({ ...savedAnswersData, ...data });
    });

    const determineQuestionRadioSkipLogic = (
        logic: IQuestionScreening,
        allValues: any
    ) => {
        const questionAnswer = allValues[`question_${logic.question_id}`];
        const answerId = questionAnswer?.answers;
        const { condition, answer_id: logicAnswerId } = logic;
        if (condition === CHOOSE) {
            return Number(answerId) === logicAnswerId;
        } else {
            return Number(answerId) !== logicAnswerId;
        }
    };

    const determineQuestionCheckboxSkipLogic = (
        logic: IQuestionScreening,
        allValues: any
    ) => {
        const questionAnswer = allValues[`question_${logic.question_id}`];
        const { answers } = questionAnswer || {};
        const { answer_id: logicAnswerId, condition } = logic;
        if (condition === CHOOSE) {
            return (
                answers && answers.length > 0 && answers.includes(logicAnswerId)
            );
        } else {
            if (!answers || answers.length === 0) return true;
            return (
                answers &&
                answers.length > 0 &&
                !answers.includes(logicAnswerId)
            );
        }
    };

    const checkScreeningForPage = (allValues: any): boolean => {
        for (const question of currentPageQuestions) {
            if (!question.creeningLogic || question.creeningLogic.length === 0)
                continue;

            let shouldScreening = false;
            question.creeningLogic.forEach((logic: IQuestionScreening) => {
                switch (question.type) {
                    case QuestionType.SingleChoice:
                        if (determineQuestionRadioSkipLogic(logic, allValues)) {
                            shouldScreening = true;
                        }
                        break;
                    case QuestionType.Checkbox:
                        if (
                            determineQuestionCheckboxSkipLogic(logic, allValues)
                        ) {
                            shouldScreening = true;
                        }
                        break;
                    default:
                        break;
                }
            });

            if (shouldScreening) return true;
        }
        return false;
    };

    // Function để fetch branch questions cho một câu hỏi
    const fetchBranchQuestion = useCallback(
        async (questionId: number) => {
            // Check cache trước
            if (branchQuestionCacheRef.current.has(questionId)) {
                const cachedQuestion =
                    branchQuestionCacheRef.current.get(questionId);
                if (cachedQuestion) return cachedQuestion;
            }

            // Check nếu đang fetch
            if (fetchingBranchQuestionsRef.current.has(questionId)) {
                return null;
            }

            try {
                // Mark as fetching
                fetchingBranchQuestionsRef.current.add(questionId);

                const response = await getQuestionBranchLogicOptions(
                    token,
                    campaignId ?? '',
                    questionId
                );

                if (response.success && response.data) {
                    // response.data.question có format JSON API: { id, type, attributes }
                    const rawQuestion = response.data.question;

                    // Sử dụng previewQuestionFormatter để format question
                    const branchQuestion = previewQuestionFormatter(
                        rawQuestion
                    ) as IQuestionPreview;

                    // Lưu vào cache
                    branchQuestionCacheRef.current.set(
                        questionId,
                        branchQuestion
                    );

                    return branchQuestion;
                }
            } catch (error) {
                console.error('Error fetching branch question:', error);
            } finally {
                // Remove from fetching set
                fetchingBranchQuestionsRef.current.delete(questionId);
            }
            return null;
        },
        [campaignId]
    );

    // useEffect để watch các câu hỏi có branchLogic và fetch branch questions khi cần
    React.useEffect(() => {
        const subscription = methods.watch((value) => {
            // Watch TẤT CẢ displayed questions (bao gồm cả branch questions)
            // Để hỗ trợ nested branching (branch của branch)
            filteredDisplayedQuestions.forEach(async (question) => {
                // Chỉ xử lý cho Single Choice, Checkbox, Dropdown
                if (
                    ![
                        QuestionType.SingleChoice,
                        QuestionType.Checkbox,
                        QuestionType.Dropdown,
                    ].includes(question.type as QuestionType)
                ) {
                    return;
                }

                if (
                    !question.branchLogic ||
                    question.branchLogic.length === 0
                ) {
                    return;
                }

                const questionKey = `question_${question.id}`;
                const currentAnswer = value[questionKey]?.answers;

                // Lấy danh sách answer_id từ branchLogic
                const branchAnswerIds = question.branchLogic.map(
                    (logic: IQuestionBranch) => logic.answer_id
                );

                let shouldFetchBranch = false;
                let branchQuestionIds: number[] = [];

                if (question.type === QuestionType.SingleChoice) {
                    // Kiểm tra nếu answer được chọn nằm trong branchLogic
                    // Convert về number để so sánh chắc chắn
                    const currentAnswerNum = Number(currentAnswer);
                    const branchAnswerIdsNum = branchAnswerIds.map((id) =>
                        Number(id)
                    );

                    if (branchAnswerIdsNum.includes(currentAnswerNum)) {
                        shouldFetchBranch = true;
                        const matchedLogic = question.branchLogic.find(
                            (logic: IQuestionBranch) =>
                                Number(logic.answer_id) === currentAnswerNum
                        );
                        if (matchedLogic) {
                            branchQuestionIds = [
                                matchedLogic.branch_question_id,
                            ];
                        }
                    }
                } else if (question.type === QuestionType.Checkbox) {
                    // Với checkbox, kiểm tra nếu có answer nào trong selectedAnswers nằm trong branchLogic
                    if (
                        Array.isArray(currentAnswer) &&
                        currentAnswer.length > 0
                    ) {
                        const branchAnswerIdsNum = branchAnswerIds.map((id) =>
                            Number(id)
                        );
                        const matchedAnswers = currentAnswer.filter(
                            (answerId: number) =>
                                branchAnswerIdsNum.includes(Number(answerId))
                        );
                        if (matchedAnswers.length > 0) {
                            shouldFetchBranch = true;
                            matchedAnswers.forEach((answerId: number) => {
                                const matchedLogic = question.branchLogic.find(
                                    (logic: IQuestionBranch) =>
                                        Number(logic.answer_id) ===
                                        Number(answerId)
                                );
                                if (matchedLogic) {
                                    branchQuestionIds.push(
                                        matchedLogic.branch_question_id
                                    );
                                }
                            });
                        }
                    }
                } else if (question.type === QuestionType.Dropdown) {
                    // Dropdown tương tự SingleChoice
                    const selectedValue = Number(currentAnswer?.value);
                    const branchAnswerIdsNum = branchAnswerIds.map((id) =>
                        Number(id)
                    );
                    if (branchAnswerIdsNum.includes(selectedValue)) {
                        shouldFetchBranch = true;
                        const matchedLogic = question.branchLogic.find(
                            (logic: IQuestionBranch) =>
                                Number(logic.answer_id) === selectedValue
                        );
                        if (matchedLogic) {
                            branchQuestionIds = [
                                matchedLogic.branch_question_id,
                            ];
                        }
                    }
                }

                // Fetch branch questions nếu cần

                if (shouldFetchBranch && branchQuestionIds.length > 0) {
                    const fetchedBranches: IQuestionPreview[] = [];
                    for (const branchQId of branchQuestionIds) {
                        const branchQ = await fetchBranchQuestion(branchQId);
                        if (branchQ) {
                            fetchedBranches.push(branchQ);
                        }
                    }

                    if (fetchedBranches.length > 0) {
                        setBranchQuestions((prev) => {
                            // Kiểm tra xem đã có branch cho question này chưa
                            const existingBranches = prev[question.id] || [];

                            // So sánh để tránh set lại nếu giống nhau
                            const existingIds = existingBranches
                                .map((b) => b.id)
                                .sort()
                                .join(',');
                            const newIds = fetchedBranches
                                .map((b) => b.id)
                                .sort()
                                .join(',');

                            if (existingIds === newIds) {
                                // Không thay đổi nếu giống nhau (tránh re-render)
                                return prev;
                            }

                            return {
                                ...prev,
                                [question.id]: fetchedBranches,
                            };
                        });
                    }
                } else {
                    // Nếu không nên fetch, xóa branch questions cũ (nếu có)
                    setBranchQuestions((prev) => {
                        if (!prev[question.id]) {
                            // Không có branch nào, không cần update
                            return prev;
                        }
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { [question.id]: _, ...newState } = prev;
                        return newState;
                    });
                }
            });
        });

        return () => subscription.unsubscribe();
    }, [filteredDisplayedQuestions, methods, fetchBranchQuestion]);

    const handlePreviousPage = () => {
        if (pageHistory.length > 0) {
            const lastPageIndex = pageHistory[pageHistory.length - 1];
            setPageHistory((prev) => prev.slice(0, -1));
            setCurrentPageIndex(lastPageIndex);
        }
    };

    const pageConfigPrevRef = React.useRef<any>(undefined);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    // Refs để lưu giá trị mới nhất mà không trigger re-render
    const savedAnswersDataRef = React.useRef(savedAnswersData);
    const currentPageQuestionsRef = React.useRef(currentPageQuestions);

    React.useEffect(() => {
        savedAnswersDataRef.current = savedAnswersData;
        currentPageQuestionsRef.current = currentPageQuestions;
    });

    // Restore và clear branch questions khi chuyển page
    // MERGE 2 useEffect lại để tránh race condition
    React.useEffect(() => {
        const restoreBranchQuestions = async () => {
            // Clear trước khi restore
            initializedBranchQuestionsRef.current.clear();
            fetchingBranchQuestionsRef.current.clear();

            // Lấy snapshot từ ref để tránh dependency trigger loop
            const savedDataSnapshot = { ...savedAnswersDataRef.current };
            const questions = currentPageQuestionsRef.current;

            const newBranchQuestions: Record<number, IQuestionPreview[]> = {};

            // Helper function đệ quy để restore branch questions
            const restoreBranchesForQuestion = async (
                question: IQuestionPreview
            ): Promise<void> => {
                // Chỉ xử lý cho Single Choice, Checkbox, Dropdown
                if (
                    ![
                        QuestionType.SingleChoice,
                        QuestionType.Checkbox,
                        QuestionType.Dropdown,
                    ].includes(question.type as QuestionType)
                ) {
                    return;
                }

                if (
                    !question.branchLogic ||
                    question.branchLogic.length === 0
                ) {
                    return;
                }

                const questionKey = `question_${question.id}`;
                const currentAnswer = savedDataSnapshot[questionKey]?.answers;

                if (!currentAnswer) {
                    return;
                }

                // Xác định branch question IDs cần restore
                let branchQuestionIds: number[] = [];

                if (question.type === QuestionType.SingleChoice) {
                    const currentAnswerNum = Number(currentAnswer);
                    const matchedLogic = question.branchLogic.find(
                        (logic: IQuestionBranch) =>
                            Number(logic.answer_id) === currentAnswerNum
                    );
                    if (matchedLogic) {
                        branchQuestionIds = [matchedLogic.branch_question_id];
                    }
                } else if (
                    question.type === QuestionType.Checkbox ||
                    question.type === QuestionType.Dropdown
                ) {
                    const selectedAnswers = Array.isArray(currentAnswer)
                        ? currentAnswer
                        : [];
                    const matchedLogics = question.branchLogic.filter(
                        (logic: IQuestionBranch) =>
                            selectedAnswers.some(
                                (ans) => Number(ans) === Number(logic.answer_id)
                            )
                    );
                    branchQuestionIds = matchedLogics.map(
                        (logic: IQuestionBranch) => logic.branch_question_id
                    );
                }

                // Fetch branch questions (sử dụng cache nếu có)
                if (branchQuestionIds.length > 0) {
                    const fetchedBranches = await Promise.all(
                        branchQuestionIds.map((id) => fetchBranchQuestion(id))
                    );
                    const validBranches = fetchedBranches.filter(
                        (b): b is IQuestionPreview => b !== null
                    );

                    if (validBranches.length > 0) {
                        newBranchQuestions[question.id] = validBranches;

                        // ĐỆQUY: restore branches của mỗi branch question
                        for (const branchQ of validBranches) {
                            await restoreBranchesForQuestion(branchQ);
                        }
                    }
                }
            };

            // Bắt đầu restore từ parent questions
            for (const question of questions) {
                await restoreBranchesForQuestion(question);
            }

            // Set branch questions VÀ restore form values CÙNG LÚC để tránh nhấp nháy
            if (Object.keys(newBranchQuestions).length > 0) {
                // Prepare form values trước khi set state
                const formValuesToRestore: Record<string, any> = {};
                Object.values(newBranchQuestions)
                    .flat()
                    .forEach((branchQ) => {
                        const questionKey = `question_${branchQ.id}`;
                        const savedAnswer = savedDataSnapshot[questionKey];

                        if (savedAnswer) {
                            formValuesToRestore[questionKey] = savedAnswer;
                        }

                        // Mark as initialized TRƯỚC khi set state
                        // Để register effect không override
                        initializedBranchQuestionsRef.current.add(branchQ.id);
                    });

                // Set state một lần duy nhất
                setBranchQuestions(newBranchQuestions);

                // Restore form values ngay lập tức sau khi setBranchQuestions
                // Dùng queueMicrotask để đảm bảo state đã flush và register effect đã chạy
                queueMicrotask(() => {
                    Object.entries(formValuesToRestore).forEach(
                        ([questionKey, savedAnswer]) => {
                            methods.setValue(questionKey, savedAnswer, {
                                shouldValidate: false,
                                shouldDirty: false,
                            });
                        }
                    );
                });
            } else {
                // Không có branch questions để restore, clear luôn
                setBranchQuestions({});
            }
        };

        restoreBranchQuestions();
        // Chỉ depend vào currentPageIndex và fetchBranchQuestion (stable)
        // savedAnswersData và currentPageQuestions được lấy từ ref
    }, [currentPageIndex, fetchBranchQuestion, methods]);

    React.useEffect(() => {
        const pageConfigQuestion = currentPageQuestions.find(
            (q) => q.isPageConfig === true
        );
        if (!pageConfigQuestion) return;

        const currentAnswer =
            methods.getValues()[`question_${pageConfigQuestion.id}`]?.answers;

        // lưu giá trị ban đầu rồi bỏ qua lần mount đầu
        if (pageConfigPrevRef.current === undefined) {
            pageConfigPrevRef.current = currentAnswer;
            return;
        }

        // nếu khác => người dùng đã thay đổi câu phân loại => clear data pages after page 1
        if (pageConfigPrevRef.current !== currentAnswer) {
            // xóa saved answers cho các question thuộc page index > 0
            setSavedAnswersData((prev) => {
                const next = { ...prev };
                Object.keys(prev).forEach((fieldKey) => {
                    const qId = Number(fieldKey.split('_')[1]);
                    if (Number.isNaN(qId)) return;
                    const pageIndexOfQ = pagesData.findIndex((p) =>
                        (p.questions || []).some((qq) => qq.id === qId)
                    );
                    if (pageIndexOfQ > 0) {
                        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                        delete next[fieldKey];
                    }
                });
                return next;
            });

            // reset branching queue and history beyond page 0
            setBranchingQueue([]);
            setPageHistory((prev) => prev.filter((idx) => idx === 0));
        }

        pageConfigPrevRef.current = currentAnswer;
    }, [currentPageQuestions, pagesData, methods]);

    const handleNextPage = async () => {
        if (isSubmittingRef.current) {
            return;
        }
        // CHỈ validate những questions đang HIỂN THỊ (trong filteredDisplayedQuestions)
        // Bao gồm cả parent questions và branch questions

        // Tách parent questions và branch questions từ filteredDisplayedQuestions
        const parentQuestionIds = currentPageQuestions.map((q) => q.id);
        const visibleParentQuestions = filteredDisplayedQuestions.filter((q) =>
            parentQuestionIds.includes(q.id)
        );
        const visibleBranchQuestions = filteredDisplayedQuestions.filter(
            (q) => !parentQuestionIds.includes(q.id)
        );

        // Validate visible parent questions (có trong schema)
        const parentQuestionKeys = visibleParentQuestions.map(
            (q) => `question_${q.id}`
        );
        const parentValidationResults = await Promise.all(
            parentQuestionKeys.map((key) => methods.trigger(key))
        );

        // Validate visible branch questions thủ công (không có trong schema)
        const branchValidationResults: {
            isValid: boolean;
            questionId: number;
        }[] = [];

        for (const branchQ of visibleBranchQuestions) {
            const questionKey = `question_${branchQ.id}`;
            const currentValue = methods.getValues()[questionKey];

            // Tạo validation schema cho branch question này
            const { validationSchema: branchSchema } =
                createValidationSchemaAndDefaults([branchQ]);

            try {
                // Validate value với schema
                await branchSchema.validateAt(
                    questionKey,
                    { [questionKey]: currentValue },
                    { abortEarly: false }
                );
                branchValidationResults.push({
                    isValid: true,
                    questionId: branchQ.id,
                });
            } catch (err: any) {
                // Set error vào form state
                if (err.inner && err.inner.length > 0) {
                    err.inner.forEach((error: any) => {
                        const fieldPath = error.path;
                        methods.setError(fieldPath, {
                            type: 'manual',
                            message: error.message,
                        });
                    });
                } else {
                    methods.setError(`${questionKey}.answers`, {
                        type: 'manual',
                        message: err.message || textRequire,
                    });
                }
                branchValidationResults.push({
                    isValid: false,
                    questionId: branchQ.id,
                });
            }
        }

        const isValid =
            parentValidationResults.every((result) => result) &&
            branchValidationResults.every((result) => result.isValid);

        if (!isValid) {
            // Tìm question đầu tiên có lỗi theo thứ tự hiển thị trên UI
            let firstErrorQuestionId: number | null = null;

            // Duyệt qua filteredDisplayedQuestions theo thứ tự hiển thị
            for (const question of filteredDisplayedQuestions) {
                const fieldKey = `question_${question.id}`;
                const fs = methods.getFieldState(`${fieldKey}.answers`);

                // Check error từ form state hoặc từ branchValidationResults
                const hasBranchError = branchValidationResults.some(
                    (r) => r.questionId === question.id && !r.isValid
                );

                if (fs?.error || hasBranchError) {
                    firstErrorQuestionId = question.id;
                    break;
                }
            }

            // Scroll đến question có lỗi trong scroll container
            if (firstErrorQuestionId && scrollContainerRef.current) {
                const el = document.getElementById(
                    `question-${firstErrorQuestionId}`
                );
                if (el) {
                    setTimeout(() => {
                        const container = scrollContainerRef.current;
                        if (container) {
                            const containerRect =
                                container.getBoundingClientRect();
                            const elementRect = el.getBoundingClientRect();
                            const scrollTop =
                                container.scrollTop +
                                elementRect.top -
                                containerRect.top -
                                100; // offset 100px từ top

                            container.scrollTo({
                                top: scrollTop,
                                behavior: 'smooth',
                            });
                        }
                    }, 100);
                }
            }
            return;
        }
        if (scrollContainerRef.current) {
            setTimeout(() => {
                scrollContainerRef.current?.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                });
            }, 0);
        }

        const currentValues = methods.getValues();

        // Lưu answers của displayed questions (bao gồm cả branch questions)
        const displayedQuestionKeys = filteredDisplayedQuestions.map(
            (q) => `question_${q.id}`
        );
        const currentPageAnswers: Record<string, any> = {};
        displayedQuestionKeys.forEach((fieldName: string) => {
            currentPageAnswers[fieldName] = currentValues[fieldName];
        });

        setSavedAnswersData((prev) => ({
            ...prev,
            ...currentPageAnswers,
        }));

        // Check screening
        if (checkScreeningForPage(currentValues)) {
            await submitAllAnswers({
                ...savedAnswersData,
                ...currentPageAnswers,
            });
            return;
        }

        // Handle page branching - kiểm tra page hiện tại có pageConfig không
        // ...existing code inside handleNextPage...
        const pageConfigQuestion = currentPageQuestions.find(
            (q) => q.isPageConfig === true
        );
        if (pageConfigQuestion?.isPageConfig) {
            const qKey = `question_${pageConfigQuestion.id}`;
            const answerValue = (currentValues as any)[qKey]?.answers;

            let targetPageIds: number[] = [];

            if (pageConfigQuestion.type === QuestionType.SingleChoice) {
                // compare as strings to avoid number/string mismatch
                const selectedAnswer = (pageConfigQuestion.answers || []).find(
                    (a: any) => String(a.id) === String(answerValue)
                );
                if (selectedAnswer?.question_page_id) {
                    targetPageIds = [selectedAnswer.question_page_id];
                }
            } else if (pageConfigQuestion.type === QuestionType.Checkbox) {
                if (Array.isArray(answerValue) && answerValue.length > 0) {
                    const selected = pageConfigQuestion.answers.filter(
                        (a: any) => answerValue.includes(a.id)
                    );
                    selected.forEach((a: any) => {
                        if (a.question_page_id)
                            targetPageIds.push(a.question_page_id);
                    });
                }
            }

            targetPageIds = Array.from(new Set(targetPageIds));

            if (targetPageIds.length > 0) {
                // set remaining queue once
                const nextPageId = targetPageIds[0];
                const remainingPages = targetPageIds.slice(1);
                setBranchingQueue(remainingPages);

                const pageIndex = pagesData.findIndex(
                    (p) => p.id === nextPageId
                );
                if (pageIndex >= 0) {
                    setPageHistory((prev) => [...prev, currentPageIndex]);
                    setCurrentPageIndex(pageIndex);
                    dispatch(captureTimeStampAction());
                    return;
                }
            }

            // nếu không có target pages => submit
            await submitAllAnswers({
                ...savedAnswersData,
                ...currentPageAnswers,
            });
            return;
        }

        // Xử lý queue từ checkbox multiple selection
        if (branchingQueue.length > 0) {
            const nextPageId = branchingQueue[0];
            setBranchingQueue((prev) => prev.slice(1));
            const pageIndex = pagesData.findIndex((p) => p.id === nextPageId);

            if (pageIndex >= 0) {
                setPageHistory((prev) => [...prev, currentPageIndex]);
                setCurrentPageIndex(pageIndex);
                dispatch(captureTimeStampAction());
                return;
            }
        }

        // Chỉ đi tuần tự khi KHÔNG có pageConfig question ở bất kỳ page nào trước đó
        // Kiểm tra xem có page nào trước đó có pageConfig không
        const hasPreviousPageConfig = pageHistory.some((historyIndex) => {
            const historyPage = pagesData[historyIndex];
            return historyPage?.questions?.some((q) => q.isPageConfig === true);
        });

        // Nếu không có pageConfig ở page trước và page hiện tại, cho phép đi tuần tự
        if (!hasPreviousPageConfig && !pageConfigQuestion) {
            const nextSequentialPageIndex = currentPageIndex + 1;
            if (nextSequentialPageIndex < pagesData.length) {
                setPageHistory((prev) => [...prev, currentPageIndex]);
                setCurrentPageIndex(nextSequentialPageIndex);
                dispatch(captureTimeStampAction());
                return;
            }
        }

        // Nếu không còn page nào, submit
        await submitAllAnswers({
            ...savedAnswersData,
            ...currentPageAnswers,
        });
    };

    const navigate = useNavigate();

    const closeSurvey = (campaignId: number, me: IPreviewSurveyState['me']) => {
        let urlToBack = '';
        if (me.creator) {
            urlToBack = `/admin/campaign/design/page-logic/${campaignId}`;
        } else if (me.type === 'Admin') {
            urlToBack = `/admin/dashboard`;
        }
        navigate(urlToBack);
    };
    const renderQuestion = useCallback(
        (question: IQuestionPreview) => {
            const questionKey = `question_${question.id}`;
            switch (question.type) {
                case QuestionType.Textbox:
                    return (
                        <AnswerQuestionTextbox
                            key={questionKey}
                            name={`${questionKey}.answers`}
                        />
                    );
                case QuestionType.Checkbox:
                    return (
                        <QuestionCheckbox
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={
                                Array.isArray(question.answers)
                                    ? question.answers.map((item) => ({
                                          label: item.label,
                                          value: item.id,
                                          image: item.image,
                                          type: item.type,
                                      }))
                                    : []
                            }
                        />
                    );
                case QuestionType.SingleChoice:
                    return (
                        <QuestionSingleShoice
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={
                                Array.isArray(question.answers)
                                    ? question.answers.map((item) => ({
                                          label: item.label,
                                          value: item.id,
                                          image: item.image,
                                          type: item.type,
                                      }))
                                    : []
                            }
                        />
                    );
                case QuestionType.Score:
                    return (
                        <QuestionScore
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={question.answers}
                        />
                    );
                case QuestionType.ScoreStarHalf:
                    return (
                        <QuestionScoreStar
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            defaultStar={question.defaultStar}
                            options={question.answers}
                            precision={0.5}
                        />
                    );
                case QuestionType.ScoreStarFull:
                    return (
                        <QuestionScoreStar
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            defaultStar={question.defaultStar}
                            options={question.answers}
                            precision={1}
                        />
                    );
                case QuestionType.SliderScale:
                    return (
                        <QuestionSliderScale
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            configs={{
                                step: question.step ?? 10,
                                scale: question.scale ?? 100,
                            }}
                            options={question.answers}
                        />
                    );
                case QuestionType.Rank:
                    return (
                        <QuestionRank
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={question.answers}
                        />
                    );
                case QuestionType.MatrixCheckbox:
                    return (
                        <>
                            <QuestionMatrixCheckbox
                                key={questionKey}
                                name={`${questionKey}.answers`}
                                options={question.answers.matrix}
                                rowsTitle={question.answers.rowsTitle}
                                columnsTitle={question.answers.columnsTitle}
                                matrixAnswerType={
                                    question.logics?.matrixAnswerType
                                }
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
                case QuestionType.MatrixRadio:
                    return (
                        <>
                            <QuestionMatrixRadio
                                key={questionKey}
                                name={`${questionKey}.answers`}
                                options={question.answers.matrix}
                                rowsTitle={question.answers.rowsTitle}
                                columnsTitle={question.answers.columnsTitle}
                                matrixAnswerType={
                                    question.logics?.matrixAnswerType
                                }
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
                case QuestionType.MatrixText:
                    return (
                        <>
                            <QuestionMatrixTextbox
                                key={questionKey}
                                name={`${questionKey}.answers`}
                                options={question.answers.matrix}
                                rowsTitle={question.answers.rowsTitle}
                                columnsTitle={question.answers.columnsTitle}
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
                case QuestionType.Dropdown:
                    return (
                        <QuestionDropdown
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={
                                Array.isArray(question.answers)
                                    ? question.answers.map((item) => ({
                                          label: item.label,
                                          value: item.id,
                                      }))
                                    : []
                            }
                        />
                    );
                case QuestionType.Number:
                    return (
                        <QuestionNumber
                            key={questionKey}
                            name={`${questionKey}.answers`}
                        />
                    );
                case QuestionType.TextboxRecommend:
                    return (
                        <>
                            <QuestionTextboxRecommend
                                key={questionKey}
                                name={`${questionKey}.answers`}
                                options={
                                    Array.isArray(question.answers)
                                        ? question.answers.map((item) => ({
                                              label: item.label,
                                              value: item.id,
                                          }))
                                        : []
                                }
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.value?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
                case QuestionType.TextboxRecommendMultiChoice:
                    return (
                        <QuestionTextboxRecommendMultiChoice
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            options={
                                Array.isArray(question.answers)
                                    ? question.answers.map((item) => ({
                                          label: item.label,
                                          value: item.id,
                                      }))
                                    : []
                            }
                        />
                    );
                case QuestionType.Date:
                    return (
                        <>
                            <QuestionDate
                                key={questionKey}
                                name={`${questionKey}.answers`}
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
                case QuestionType.Image:
                    return (
                        <QuestionImage
                            key={questionKey}
                            name={`${questionKey}.answers`}
                            maxFileSize={fileMaxSize}
                        />
                    );
                case QuestionType.ImageWithTitle:
                    return (
                        <>
                            <QuestionImageWithTitle
                                key={questionKey}
                                name={`${questionKey}.answers`}
                                options={question.answers.images}
                                maxFileSize={fileMaxSize}
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
                case QuestionType.Emoji:
                    return (
                        <>
                            <QuestionEmoji
                                key={questionKey}
                                name={`${questionKey}.answers`}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: [
                                        'space-between',
                                        'flex-start',
                                    ],
                                    gap: [0, 5],
                                }}
                                options={question.answers}
                                numberOfEmojis={question.numberOfEmojis ?? 0}
                            />
                            <Box sx={{ marginTop: '1rem' }}>
                                {errors[questionKey] && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.textError,
                                            fontSize: '14px',
                                            fontWeight: '400',
                                        }}
                                    >
                                        {
                                            (errors[questionKey] as any)
                                                ?.answers?.message
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </>
                    );
            }
        },
        [errors]
    );

    return (
        <Box component="div" tabIndex={0}>
            <Grid container sx={{ position: 'relative' }}>
                <Grid
                    item
                    xs={12}
                    md={8}
                    sx={{
                        padding: ['2rem', '2rem', '2rem', '4rem', '4rem'],
                        backgroundImage: [
                            `url(${surveyImage ?? bgQuestionSingleChoice})`,
                            'unset',
                        ],
                        backgroundColor: ['unset', '#EFF1FF'],
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        height: '100vh',
                        overflow: 'auto',
                    }}
                    ref={scrollContainerRef}
                >
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Grid
                            container
                            sx={{
                                marginBottom: [
                                    '1rem',
                                    '1rem',
                                    '1rem',
                                    '3rem',
                                    '3rem',
                                ],
                                display: ['none', 'flex'],
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Grid item md={4}>
                                <BrandLogo width={'130px'} />
                            </Grid>
                            <Grid
                                item
                                md={8}
                                sm={8}
                                xs={12}
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    justifyContent: 'flex-end',
                                    marginTop: 6,
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    onClick={() => {
                                        setSurveyStep && setSurveyStep(0);
                                    }}
                                    variant="contained"
                                    sx={styles.btn}
                                >
                                    Bắt đầu lại
                                </Button>
                                {me.creator && (
                                    <IconButton
                                        onClick={() =>
                                            closeSurvey(
                                                Number(surveyState.id),
                                                me
                                            )
                                        }
                                        sx={{
                                            backgroundColor: colors.main,
                                            '&:hover': {
                                                backgroundColor: colors.main,
                                            },
                                            width: [
                                                '2.5rem',
                                                '2rem',
                                                '2rem',
                                                '3rem',
                                                '3rem',
                                            ],
                                            height: [
                                                '2.5rem',
                                                '2rem',
                                                '2rem',
                                                '3rem',
                                                '3rem',
                                            ],
                                        }}
                                    >
                                        <CloseIcon
                                            sx={{ color: 'common.white' }}
                                        />
                                    </IconButton>
                                )}
                            </Grid>
                        </Grid>

                        <Grid container marginBottom={2}>
                            <Grid item md={12}>
                                <Typography
                                    component="span"
                                    variant="subtitle2"
                                    sx={{
                                        display: 'inline-block',
                                        backgroundColor: 'primary.main',
                                        color: 'var(--color-primary-lighter)',
                                        px: 2,
                                        py: '12px',
                                        borderRadius: '999px',
                                        fontWeight: 700,
                                        fontSize: ['0.9rem', '1rem', '1.1rem'],
                                        lineHeight: 1,
                                    }}
                                >
                                    {currentPage.title}
                                </Typography>
                            </Grid>
                        </Grid>

                        {filteredDisplayedQuestions.map(
                            (question, displayIndex) => {
                                // Đánh số liên tục cho tất cả questions (bao gồm cả branch)
                                const questionNumber = displayIndex + 1;

                                return (
                                    <Box
                                        key={question.id}
                                        id={`question-${question.id}`}
                                        sx={{ marginBottom: '3rem' }}
                                    >
                                        <BaseTextField
                                            name={`question_${question.id}.questionId`}
                                            key={`question_${question.id}.questionId`}
                                            sx={{ display: 'none' }}
                                        />
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                color: [
                                                    welcomeTextColor,
                                                    'primary.main',
                                                ],
                                                fontWeight: 700,
                                                fontSize: [
                                                    '1.3rem',
                                                    '1.5rem',
                                                    '1.6rem',
                                                    '2rem',
                                                    '2rem',
                                                ],
                                                marginBottom: '1.5rem',
                                            }}
                                        >
                                            {questionNumber}.{' '}
                                            {question.question}{' '}
                                            <>
                                                {question.isPageConfig && (
                                                    <LinkIcon
                                                        sx={{
                                                            fontSize: [
                                                                '1.3rem',
                                                                '1.5rem',
                                                                '1.6rem',
                                                                '2rem',
                                                                '2rem',
                                                            ],
                                                            verticalAlign:
                                                                'middle',
                                                        }}
                                                    />
                                                )}
                                            </>
                                            {question.required && (
                                                <span
                                                    style={{
                                                        color: colors.textError,
                                                    }}
                                                >
                                                    {' '}
                                                    *
                                                </span>
                                            )}
                                        </Typography>
                                        <Box>{renderQuestion(question)}</Box>
                                    </Box>
                                );
                            }
                        )}

                        <Grid
                            container
                            marginTop={5}
                            sx={{
                                display: ['flex'],
                            }}
                        >
                            <Grid
                                item
                                md={12}
                                display={'flex'}
                                justifyContent={'flex-start'}
                                gap={2}
                            >
                                <Button
                                    variant="outlined"
                                    disabled={
                                        currentPageIndex === 0 &&
                                        pageHistory.length === 0
                                    }
                                    onClick={handlePreviousPage}
                                    sx={{
                                        borderRadius: '10px',
                                        width: '42px',
                                        height: '42px',
                                        minWidth: '0',
                                        padding: '0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ArrowBackIosNewIcon sx={{ mr: 1 }} />
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleNextPage}
                                    disabled={isSubmitting}
                                    sx={{
                                        borderRadius: '10px',
                                        width: '42px',
                                        height: '42px',
                                        minWidth: '0',
                                        padding: '0',
                                    }}
                                >
                                    <ArrowForwardIosIcon sx={{ ml: 1 }} />
                                </Button>
                            </Grid>
                        </Grid>
                    </FormProvider>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                        backgroundImage: `url(${surveyImage ?? bgQuestionSingleChoice})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'bottom',
                        backgroundSize: 'cover',
                        padding: [
                            '2rem 3rem',
                            '2rem 3rem',
                            '2rem 3rem',
                            '3rem 4rem',
                            '3rem 4rem',
                        ],
                        display: ['none', 'none', 'flex'],
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        height: '100vh',
                        overflowY: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            fontWeight: '900',
                            fontSize: [
                                '3rem',
                                '3rem',
                                '2.5rem',
                                '3.5rem',
                                '3.5rem',
                            ],
                            lineHeight: [
                                '2rem',
                                '3rem',
                                '3rem',
                                '4rem',
                                '4rem',
                            ],
                            color: welcomeTextColor,
                        }}
                    >
                        {surveyState.title}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

const styles = createStyleSheet({
    btn: {
        minWidth: ['5rem', '7rem', '7rem', '12rem', '13rem'],
        height: ['2.5rem', '2rem', '2rem', '3rem', '3rem'],
        textTransform: 'none',
        borderRadius: '40px',
        fontSize: ['0.6rem', '0.7rem', '0.7rem', '1rem', '1rem'],
        fontWeight: '700',
        padding: ['1rem', '1rem', '1rem', '1.5rem 3rem'],
    },
});

export default AnswerTheSurvey;
